/**
 * Data-export TanStack Query hooks.
 *
 * Two flavours: actor self-export (zip of every take they recorded) and org
 * owner export (all projects, episodes, scenes, cues, characters, contracts,
 * milestones, comments, audit subset).
 *
 * The API kicks off the job async and returns 202 + a job_id; the UI then
 * polls GET /api/v1/me/data-export/:job_id (or the org variant) until the
 * status flips to `completed` or `failed`. The completed payload carries a
 * 24-hour presigned download URL pointing into the archive R2 bucket.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

export type DataExportStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface DataExportJob {
  job_id: string
  status: DataExportStatus
  scope: 'user' | 'org'
}

export interface DataExportJobStatus extends DataExportJob {
  /** 24-hour presigned R2 URL when status='completed', else null. */
  url: string | null
  /** Matching expiry (ISO-8601) for `url`. */
  expires_at: string | null
  size_bytes: number | null
  /** Human-readable error when status='failed'. */
  error: string | null
  created_at: string
  completed_at: string | null
}

const meExportKey = (jobId: string) => ['data-export', 'me', jobId] as const
const orgExportKey = (orgId: string, jobId: string) => ['data-export', 'org', orgId, jobId] as const

/**
 * Kick off an actor data export. Returns the job_id used for polling.
 * The caller is responsible for stashing the job_id (e.g. localStorage)
 * so the UI can resume polling on reload.
 */
export function useStartMyDataExport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<DataExportJob>('/api/v1/me/data-export', { method: 'POST' }),
    onSuccess: (job) => {
      // Seed the polling cache so the consumer doesn't render a "loading"
      // flash before the first GET lands.
      qc.setQueryData<DataExportJobStatus>(meExportKey(job.job_id), {
        ...job,
        url: null,
        expires_at: null,
        size_bytes: null,
        error: null,
        created_at: new Date().toISOString(),
        completed_at: null,
      })
    },
  })
}

/** Poll an in-flight or completed actor export. Stops once terminal. */
export function useMyDataExportStatus(jobId: string | null) {
  return useQuery({
    queryKey: jobId ? meExportKey(jobId) : ['data-export', 'me', 'noop'],
    queryFn: () => api<DataExportJobStatus>(`/api/v1/me/data-export/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const data = q.state.data as DataExportJobStatus | undefined
      if (!data) return 3000
      return data.status === 'completed' || data.status === 'failed' ? false : 3000
    },
  })
}

/** Owner-only — kick off an org-wide export. */
export function useStartOrgDataExport(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api<DataExportJob>(`/api/v1/orgs/${orgId}/data-export`, { method: 'POST' }),
    onSuccess: (job) => {
      qc.setQueryData<DataExportJobStatus>(orgExportKey(orgId, job.job_id), {
        ...job,
        url: null,
        expires_at: null,
        size_bytes: null,
        error: null,
        created_at: new Date().toISOString(),
        completed_at: null,
      })
    },
  })
}

export function useOrgDataExportStatus(orgId: string, jobId: string | null) {
  return useQuery({
    queryKey: jobId ? orgExportKey(orgId, jobId) : ['data-export', 'org', orgId, 'noop'],
    queryFn: () => api<DataExportJobStatus>(`/api/v1/orgs/${orgId}/data-export/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const data = q.state.data as DataExportJobStatus | undefined
      if (!data) return 3000
      return data.status === 'completed' || data.status === 'failed' ? false : 3000
    },
  })
}
